import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {FormControl, Grid, InputLabel, Select, SelectChangeEvent, TextField, Card, CardContent, Chip, Stack} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ImageIcon from '@mui/icons-material/Image';
import AlbumImageUpload from "./AlbumImageUpload";
import {setAlbum} from "../../../Services/ApiCalls";

const steps = ["Enter Album Details", "Upload Images", "Review & Submit"];

export default function AlbumCreationStepper() {
    const [activeStep, setActiveStep] = React.useState(0);
    const [completed, setCompleted] = React.useState<{
        [k: number]: boolean;
    }>({});
    const [albumData, setAlbumData] = React.useState({
        title: '',
        uploadedBy: '',
        description: '',
        coverImage: '',
        category: '',
    });
    const dropdownList = [
        {key: 'Radiology Info graphics', value: 'RadioInfo'},
        {key: 'Radiology Illustrations', value: 'RadioIllust'},
        {key: 'Short Video', value: 'ShortVideo'},
        {key: 'My Publications and Post work', value: 'Mypub'},
        {key: 'Blogs', value: 'Blogs'},

    ]
    const [albumId, setAlbumId] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const [imagesUploaded, setImagesUploaded] = React.useState(false);



    const totalSteps = () => {
        return steps.length;
    };

    const completedSteps = () => {
        return Object.keys(completed).length;
    };

    const isLastStep = () => {
        return activeStep === totalSteps() - 1;
    };

    const allStepsCompleted = () => {
        return completedSteps() === totalSteps();
    };

    const handleNext = async () => {
        if (activeStep === 0 && !albumId) {
            console.log("Current albumData state:", albumData);
            console.log("Field validation:", {
                title: !!albumData.title,
                description: !!albumData.description,
                uploadedBy: !!albumData.uploadedBy,
                category: !!albumData.category
            });
            
            // Validate required fields
            if (!albumData.title || !albumData.description || !albumData.uploadedBy || !albumData.category) {
                setSaveError("Please fill in all required fields: Title, Description, Uploaded By, and Category");
                return;
            }
            
            setSaveError(null);
            setSaving(true);
            try{
                console.log("Creating album with data:", albumData);
                const res = await setAlbum(
                    albumData.title,
                    albumData.description,
                    albumData.coverImage || '', // Use empty string if no cover image
                    albumData.uploadedBy,
                    albumData.category,
                );
                console.log("Album creation response:", res);
                const id = (res as any).album ?? (res as any).albumId;
                console.log("Extracted album ID:", id, "Type:", typeof id);
                if (!id) throw new Error("Failed to create album - no ID returned");
                const stringId = String(id);
                console.log("Setting albumId to:", stringId);
                setAlbumId(stringId); // Convert to string to ensure proper URL handling
                setSaving(false);
            }catch (e:any){
                console.error("Album creation error:", e);
                setSaveError(e?.message || "Failed to create album");
                setSaving(false);
                return;
            }
        }
        
        // Handle step 2 to step 3 transition - mark images as uploaded
        if (activeStep === 1) {
            setImagesUploaded(true);
        }
        
        // Mark current step as completed
        setCompleted(prev => ({
            ...prev,
            [activeStep]: true
        }));
        
        const newActiveStep =
            isLastStep() && !allStepsCompleted()
                ? // It's the last step, but not all steps have been completed,
                  // find the first step that has been completed
                steps.findIndex((step, i) => !(i in completed))
                : activeStep + 1;
        setActiveStep(newActiveStep);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStep = (step: number) => () => {
        setActiveStep(step);
    };

    const handleComplete = () => {
        setCompleted({
            ...completed,
            [activeStep]: true,
        });
        handleNext();
    };

    const handleReset = () => {
        setActiveStep(0);
        setCompleted({});
        setAlbumId(null);
        setImagesUploaded(false);
        setSaveError(null);
        setSaving(false);
    };

    const handleCategoryChange = (e: SelectChangeEvent<string>) => {
        setAlbumData({...albumData, category: e.target.value as string});
    };

    return (
        <Box sx={{width: '100%'}}>
            <Stepper nonLinear activeStep={activeStep}>
                {steps.map((label, index) => (
                    <Step key={label} completed={completed[index]}>
                        <StepButton color="inherit" onClick={handleStep(index)}>
                            {label}
                        </StepButton>
                    </Step>
                ))}
            </Stepper>

            <div>
                {allStepsCompleted() ? (
                    <React.Fragment>
                        <Box sx={{mt: 2, mb: 1}}>
                            <Typography variant="h6" gutterBottom>
                                🎉 ImageBlog created successfully!
                            </Typography>
                            <Card sx={{mb: 2}}>
                                <CardContent>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                                        <CheckCircleIcon color="success" />
                                        <Typography variant="h6">ImageBlog Ready</Typography>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        Your ImageBlog album has been created with all images uploaded successfully.
                                    </Typography>
                                    {albumId && (
                                        <Typography variant="caption" display="block" sx={{mt: 1}}>
                                            Album ID: {albumId}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>
                        <Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
                            <Box sx={{flex: '1 1 auto'}}/>
                            <Button onClick={handleReset} variant="outlined">Create Another ImageBlog</Button>
                        </Box>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        {activeStep === 0 && (
                            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                <Typography variant="h6" gutterBottom>
                                    Enter Album Details
                                </Typography>
                                {saveError && (
                                    <Typography color="error" sx={{mb: 2}}>
                                        {saveError}
                                    </Typography>
                                )}
                                <Grid container spacing={2}>
                                    <Grid size={{lg: 4, xs: 12, md: 4}} key={1} display="flex">
                                        <TextField
                                            fullWidth
                                            label="Album Title"
                                            name="title"
                                            value={albumData.title}
                                            onChange={(e) =>
                                                setAlbumData({
                                                    ...albumData,
                                                    title: e.target.value,
                                                })
                                            }/>
                                    </Grid>
                                    <Grid size={{lg: 4, xs: 12, md: 4}} display="flex">
                                        <TextField
                                            fullWidth
                                            label="Uploaded By"
                                            name="uploadedBy"
                                            value={albumData.uploadedBy}
                                            onChange={(e) =>
                                                setAlbumData({
                                                    ...albumData,
                                                    uploadedBy: e.target.value,
                                                })
                                            }/>
                                    </Grid>
                                    <Grid size={{lg: 4, xs: 12, md: 4}} display="flex">
                                        <TextField
                                            fullWidth
                                            label="Description"
                                            name="description"
                                            value={albumData.description}
                                            onChange={(e) =>
                                                setAlbumData({
                                                    ...albumData,
                                                    description: e.target.value,
                                                })
                                            }/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 6}}>
                                        <FormControl fullWidth>
                                            <InputLabel id="category-label">Category</InputLabel>
                                            <Select
                                                labelId="category-label"
                                                id="category-select"
                                                label="Category"
                                                value={albumData.category}
                                                onChange={handleCategoryChange}
                                            >
                                                {dropdownList.map((item) => (
                                                    <MenuItem key={item.value} value={item.value}>
                                                        {item.key}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                        {activeStep === 1 && (
                            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                <Grid container spacing={2}>
                                    <Grid size={{lg: 4, xs: 12, md: 4}} key={1} display="flex">
                                        {activeStep === 1 && albumId && (
                                            <AlbumImageUpload albumId={albumId} />
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                        {activeStep === 2 && (
                            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                <Typography variant="h6" gutterBottom>
                                    Review & Submit
                                </Typography>
                                
                                <Grid container spacing={2}>
                                    <Grid size={{xs: 12}}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    ImageBlog Details
                                                </Typography>
                                                <Stack spacing={1}>
                                                    <Typography><strong>Title:</strong> {albumData.title}</Typography>
                                                    <Typography><strong>Description:</strong> {albumData.description}</Typography>
                                                    <Typography><strong>Uploaded By:</strong> {albumData.uploadedBy}</Typography>
                                                    <Typography><strong>Category:</strong> {albumData.category}</Typography>
                                                    <Typography><strong>Album ID:</strong> {albumId}</Typography>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    
                                    <Grid size={{xs: 12}}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Image Upload Status
                                                </Typography>
                                                {imagesUploaded ? (
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <CheckCircleIcon color="success" />
                                                        <Chip 
                                                            label="Images Uploaded" 
                                                            color="success" 
                                                            variant="outlined"
                                                            icon={<ImageIcon />}
                                                        />
                                                        <Typography variant="body2" color="text.secondary">
                                                            Ready for publication
                                                        </Typography>
                                                    </Stack>
                                                ) : (
                                                    <Typography color="text.secondary">
                                                        Images will be uploaded when you proceed
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                        <Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
                            <Button
                                color="inherit"
                                disabled={activeStep === 0}
                                onClick={handleBack}
                                sx={{mr: 1}}
                            >
                                Back
                            </Button>
                            <Box sx={{flex: '1 1 auto'}}/>
                            <Button 
                                onClick={handleNext} 
                                sx={{mr: 1}}
                                disabled={saving}
                            >
                                {saving ? 'Creating Album...' : 
                                 activeStep === 2 ? 'Complete ImageBlog' : 'Next'}
                            </Button>
                            {activeStep !== steps.length &&
                                (completed[activeStep] ? (
                                    <Typography variant="caption" sx={{display: 'inline-block'}}>
                                        Step {activeStep + 1} already completed
                                    </Typography>
                                ) : (
                                    <Button onClick={handleComplete}>
                                        {completedSteps() === totalSteps() - 1
                                            ? 'Finish'
                                            : 'Complete Step'}
                                    </Button>
                                ))}
                        </Box>

                    </React.Fragment>
                )}

            </div>
        </Box>
    );
}
